import {useEffect, useState} from 'react';
import {AppBar, Box} from '@mui/material';
import LongMenu from './Menu';
import {IUser} from '../../models/IUser';
import {authAPI} from '../../services/AuthServices';
import {useLocation, useNavigate} from 'react-router-dom';
import {useStyles} from './headerStyles';
import {ThemeSwitcher} from './ThemeSwitcher';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import {IconButton, Tooltip} from '@mui/material';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';

interface Props {
    user: IUser | undefined;
    canOpenAdmin: boolean;
    onLoggedOut: () => void;
}

function Header({user, canOpenAdmin, onLoggedOut}: Props) {
    const [workshopName, setWorkshopName] = useState('');

    const history = useNavigate();
    const location = useLocation();
    const {t} = useAppTranslation();
    const isAdmin = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
    const {menuWrapper, bar, brandLogo, headerWrapper, leftSideWrapper, rightSideWrapper, nameStyle} = useStyles(user);

    // api
    const [logOut] = authAPI.useLogOutMutation();

    // get user name if needed
    const extractName = (str: string) => {
        return str.replace(/^(.)(.*)$/, (_, firstLetter, rest) => {
            const res = firstLetter.toUpperCase() + rest.toLowerCase();
            return res.split('@')[0];
        });
    };


    // logout method
    const handleLogOut = () => {
        logOut().finally(() => {
            history('/');
            setWorkshopName('');
            onLoggedOut();
        });
    };

    // router
    const router = () => {
        history('/app');
    };

    // Show the workshop name; keep the account name as a fallback for users
    // who have not saved their settings yet.
    useEffect(() => {
        if (!user) {
            setWorkshopName('');
            return;
        }

        const fallbackName = user.name.includes('@') ? extractName(user.name) : user.name;
        setWorkshopName(user.workshopName?.trim() || fallbackName);
    }, [user]);

    return (
        <AppBar sx={bar}>
                <Box sx={headerWrapper}>
                <Box sx={{...leftSideWrapper, ...menuWrapper}} onClick={router}>
                    <Box component="img" src={`${process.env.PUBLIC_URL}/branding/mybake-wordmark-3200px.png`} alt="MyBake.pro" width={3200} height={712} sx={brandLogo}/>
                </Box>

                <Box sx={rightSideWrapper}>
                    <Box sx={nameStyle}>{workshopName}</Box>
                    {!!user && <ThemeSwitcher/>}
                    {canOpenAdmin && <Tooltip title={isAdmin ? t('auth:return_to_app') : t('admin:dashboard.title' as never)}><IconButton aria-label={isAdmin ? t('auth:return_to_app') : t('admin:dashboard.title' as never)} onClick={() => history(isAdmin ? '/app' : '/admin')} sx={{display: {xs: 'none', md: 'inline-flex'}}}>{isAdmin ? <AppsOutlinedIcon/> : <AdminPanelSettingsOutlinedIcon/>}</IconButton></Tooltip>}
                    <Box sx={menuWrapper}>
                        <LongMenu logout={handleLogOut} canOpenAdmin={canOpenAdmin} isAdmin={isAdmin}/>
                    </Box>
                </Box>
            </Box>
        </AppBar>
    );
}

export default Header;
